import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { timelyIntegrationCloudV1UserAccountsSchema } from '@timelyapp/timely-api-spec';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { GenericTimelyAccountId, OKGeneric } from '../models';
import { SyncTimelyToTicResponseSchema, TimelyWebhookHeadersSchema } from '../models/models.sync';

extendZodWithOpenApi(z);

const c = initContract();

export const apiSync = c.router({
  identityTokenExchange: {
    method: 'POST',
    path: `/1.0/sync/identity-token-exchange`,
    description: `You can get a Timely token by visiting [https://app.timelyapp.com/oauth/token.json](https://app.timelyapp.com/oauth/token.json) when logged in`,
    body: z.object({
      token: z.string().openapi({ example: 'kS-Jo3xUhJMPIrcazb2BEEaHZuH1tBkrwXMdbEg295sM' }),
    }),
    summary:
      'Accepts a Timely token, then syncs the user/accounts to TIC. Note: Intentionally does not return tic_user/tic_accounts, but trivial to add',
    responses: {
      200: z.object({
        jwt: z.string().openapi({
          example:
            'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
        }),
        jwtValidInMinutes: z.number().openapi({ example: 60 }),
      }),
    },
  },
  timelyDiffToTic: {
    method: 'POST',
    path: `/1.0/sync/timely-diff-to-tic`,
    responses: { 200: OKGeneric },
    description:
      'Upon changes to a Timely user, account, or user-account relationship, Timely sends a webhook to this endpoint that describe the changes. \n\n The `timely_user` object must contain the user most relevant to the change (e.g, the user who creates a new account, or the user being invited to an account)\n\n The `timely_account` is optional, but **must** be included when relevant to the context of the change (which is almost always, except in some `user_*` scoped cases like `user_deleted` or `user_export`)\n\n`user_export` signals that a user wants to export their data (GDPR)\n\n**TIC will ignore diffs that do not match pre-existing TIC records while TIC remains opt-in**',
    headers: TimelyWebhookHeadersSchema,
    body: timelyIntegrationCloudV1UserAccountsSchema.extend({
      event: z.enum([
        'account_created',
        'account_updated',
        'account_deleted',
        'user_created',
        'user_updated',
        'user_deleted',
        'account_user_created',
        'account_user_updated',
        'account_user_deleted',
        'user_export',
      ]),
    }),
  },
  timelyToTic: {
    method: 'POST',
    path: `/1.0/sync/timely-to-tic`,
    summary: 'Builds the bridge between a TIC user and a Timely user and their accounts',
    description:
      'Synchronizes a Timely user and their associated accounts to TIC, creating or updating the corresponding TIC user, accounts, and identity records. This establishes the foundational link between Timely and TIC, enabling integration features. Returns a JWT for subsequent TIC API calls.',
    headers: TimelyWebhookHeadersSchema,
    query: z
      .object({
        account_id: GenericTimelyAccountId.optional().describe(
          'The Timely `account.id` the user is authenticating to. Used to ensure `origin_tic_identity_id` spawned users are properly associated with `identity` and `identity_permissions`. If not set and user only has 1 account, will default to that account.'
        ),
      })
      .passthrough(),
    body: timelyIntegrationCloudV1UserAccountsSchema
      .extend({
        hostname: z.string().optional().openapi({
          example: 'app.timelyapp.com',
          description:
            'Include the host of the Timely instance that sent the request. If not set, will always default to production/`app.timelyapp.com`',
        }),
      })
      // Allow additional properties for now, allowing Timely to pass more data without causing validation errors.
      .passthrough(),
    responses: {
      200: SyncTimelyToTicResponseSchema,
    },
  },
});
